<script>
  import CountrySelect from '../components/CountrySelect.svelte';
  import { apiFetch } from '../lib/auth.js';
  import { createEventDispatcher } from 'svelte';

  const dispatch = createEventDispatcher();

  // ── Records ────────────────────────────────────────────────────────
  export let records = [];

  // ── Form state ─────────────────────────────────────────────────────
  let showForm    = false;
  let editing     = null;
  let formError   = '';
  let formSuccess = false;
  let formLoad    = false;

  let form = blankForm();

  function blankForm() {
    return { name: '', address: '', zip: '', city: '', country: '', capacity: '', description: '' };
  }

  function openAdd() {
    form = blankForm();
    editing = null; showForm = true; formError = ''; formSuccess = false;
  }

  function openEdit(record) {
    form = {
      name:        record.name        || '',
      address:     record.address     || '',
      zip:         record.zip         || '',
      city:        record.city        || '',
      country:     record.country     || '',
      capacity:    record.capacity    != null ? String(record.capacity) : '',
      description: record.description || '',
    };
    editing = record; showForm = true; formError = ''; formSuccess = false;
    setTimeout(() => document.querySelector('.form-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  function closeForm() {
    showForm = false; editing = null; formError = ''; formSuccess = false;
  }

  async function submitForm() {
    if (!form.name.trim()) { formError = 'Name is required'; return; }
    formError = ''; formLoad = true;
    const isEdit = !!editing;
    const endpoint = isEdit ? `/venues/${editing.id}` : '/venues';
    const body = { name: form.name.trim() };
    if (form.address)     body.address     = form.address.trim();
    if (form.zip)         body.zip         = form.zip.trim();
    if (form.city)        body.city        = form.city.trim();
    if (form.country)     body.country     = form.country.trim();
    if (form.capacity)    body.capacity    = Number(form.capacity);
    if (form.description) body.description = form.description.trim();
    try {
      const result = await apiFetch(endpoint, { method: isEdit ? 'PUT' : 'POST', body: JSON.stringify(body) });
      records = isEdit
        ? records.map(r => r.id === result.id ? result : r)
        : [result, ...records];
      // Notify parent so ManageEvents can keep its venue list in sync
      dispatch('venueCreated', result);
      formSuccess = true;
      setTimeout(closeForm, 900);
    } catch (e) {
      formError = e.message || 'Failed to save';
    } finally {
      formLoad = false;
    }
  }

  // ── Delete ─────────────────────────────────────────────────────────
  let deleteLoad  = false;
  let deleteError = '';

  async function deleteRecord() {
    if (!editing) return;
    deleteError = '';
    deleteLoad  = true;
    try {
      await apiFetch(`/venues/${editing.id}`, { method: 'DELETE' });
      records = records.filter(r => r.id !== editing.id);
      closeForm();
    } catch (e) {
      deleteError = e.message || 'Failed to delete — please try again.';
    } finally {
      deleteLoad = false;
    }
  }

  // ── Stage panel ────────────────────────────────────────────────────
  let expandedVenueId = null;
  let venueStages     = {};
  let venueStageLoad  = {};
  let addStageVenueId = null;
  let stageName       = '';
  let stageCapacity   = '';
  let stageError      = '';
  let stageLoad       = false;

  async function toggleVenueExpand(venue) {
    if (expandedVenueId === venue.id) { expandedVenueId = null; return; }
    expandedVenueId = venue.id;
    if (venueStages[venue.id]) return;
    venueStageLoad[venue.id] = true;
    try {
      const data = await apiFetch(`/venues/${venue.id}/stages`);
      venueStages[venue.id] = Array.isArray(data) ? data : [];
    } catch {
      venueStages[venue.id] = [];
    } finally {
      venueStageLoad[venue.id] = false;
      venueStages = { ...venueStages };
    }
  }

  function openAddStage(venueId) {
    addStageVenueId = venueId;
    stageName = ''; stageCapacity = ''; stageError = '';
  }
  function closeAddStage() { addStageVenueId = null; }

  async function submitAddStage(venueId) {
    if (!stageName.trim()) { stageError = 'Name required'; return; }
    stageLoad = true; stageError = '';
    try {
      const body = { name: stageName.trim(), venue_id: venueId };
      if (stageCapacity) body.capacity = Number(stageCapacity);
      const created = await apiFetch('/stages', { method: 'POST', body: JSON.stringify(body) });
      venueStages[venueId] = [...(venueStages[venueId] || []), created];
      venueStages = { ...venueStages };
      closeAddStage();
    } catch (e) {
      stageError = e.message || 'Failed to add stage';
    } finally {
      stageLoad = false;
    }
  }

  async function deleteStage(venueId, stageId) {
    if (!confirm('Delete this stage?')) return;
    try {
      await apiFetch(`/stages/${stageId}`, { method: 'DELETE' });
      venueStages[venueId] = (venueStages[venueId] || []).filter(s => s.id !== stageId);
      venueStages = { ...venueStages };
    } catch {}
  }

  // ── Dedupe / merge ─────────────────────────────────────────────────
  let dedupeOpen      = false;
  let canonicalIds    = {};
  let mergeGroupIdx   = null;
  let mergeGroupLoad  = false;
  let mergeGroupError = '';

  function getDupeGroups(list) {
    const groups = {};
    for (const r of list) {
      const key = r.name.toLowerCase().trim();
      if (!groups[key]) groups[key] = [];
      groups[key].push(r);
    }
    return Object.values(groups).filter(g => g.length > 1);
  }

  $: dupeGroups = getDupeGroups(records);

  $: duplicateNames = (() => {
    const seen = {}; const dupes = new Set();
    for (const r of records) {
      const key = r.name.toLowerCase().trim();
      if (seen[key]) dupes.add(key); else seen[key] = true;
    }
    return dupes;
  })();

  function isDuplicate(r) {
    return duplicateNames.has(r.name.toLowerCase().trim());
  }

  function initCanonicals() {
    canonicalIds = {};
    dupeGroups.forEach((group, i) => {
      const scored = group.map(r => ({
        id: r.id,
        score: (r.city ? 1 : 0) + (r.country ? 1 : 0) + (r.capacity ? 1 : 0) + (r.address ? 1 : 0),
      }));
      scored.sort((a, b) => b.score - a.score);
      canonicalIds[i] = scored[0].id;
    });
  }

  function openDedupe() {
    dedupeOpen = true; initCanonicals(); mergeGroupError = '';
  }
  function closeDedupe() { dedupeOpen = false; canonicalIds = {}; }

  async function mergeGroup(groupIdx) {
    const group     = dupeGroups[groupIdx];
    const canonical = canonicalIds[groupIdx];
    if (!canonical) return;
    const duplicates = group.filter(r => r.id !== canonical);
    mergeGroupIdx = groupIdx; mergeGroupLoad = true; mergeGroupError = '';
    try {
      for (const dup of duplicates) {
        await apiFetch(`/venues/${dup.id}/merge`, {
          method: 'POST',
          body: JSON.stringify({ into_id: canonical }),
        });
        records = records.filter(r => r.id !== dup.id);
      }
      initCanonicals();
    } catch (e) {
      mergeGroupError = `Group ${groupIdx + 1}: ${e.message || 'Merge failed'}`;
    } finally {
      mergeGroupLoad = false; mergeGroupIdx = null;
    }
  }
</script>

<!-- ── List header ──────────────────────────────────────────────────── -->
{#if !showForm && !dedupeOpen}
  <div class="list-header">
    <span class="record-count">
      {records.length} records
      {#if duplicateNames.size > 0}
        <button class="dupe-badge-btn" on:click={openDedupe}>
          {dupeGroups.length} duplicate group{dupeGroups.length > 1 ? 's' : ''}
        </button>
      {/if}
    </span>
    <div class="list-header-actions">
      <button class="btn-add-top" on:click={openAdd}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        Add Venue
      </button>
    </div>
  </div>
{/if}

<!-- ── Add / Edit form ──────────────────────────────────────────────── -->
{#if showForm}
  <div class="form-panel">
    <div class="form-header">
      <span>{editing ? 'Edit Venue' : 'Add Venue'}</span>
      <button class="btn-dismiss" on:click={closeForm}>✕</button>
    </div>

    {#if formSuccess}
      <div class="form-success">✓ {editing ? 'UPDATED' : 'ADDED'}</div>
    {:else}
      {#if formError}<div class="form-error">{formError}</div>{/if}

      <div class="field">
        <label for="venue-name">Name *</label>
        <input id="venue-name" type="text" bind:value={form.name} placeholder="e.g. Kniven" />
      </div>
      <div class="field">
        <label for="venue-address">Address</label>
        <input id="venue-address" type="text" bind:value={form.address} placeholder="Møllergata 32" />
      </div>
      <div class="row">
        <div class="field">
          <label for="venue-zip">ZIP</label>
          <input id="venue-zip" type="text" bind:value={form.zip} placeholder="0179" />
        </div>
        <div class="field">
          <label for="venue-city">City</label>
          <input id="venue-city" type="text" bind:value={form.city} placeholder="Oslo" />
        </div>
      </div>
      <div class="row">
        <CountrySelect bind:value={form.country} />
        <div class="field">
          <label for="venue-capacity">Capacity</label>
          <input id="venue-capacity" type="number" bind:value={form.capacity} placeholder="400" min="1" />
        </div>
      </div>
      <div class="field">
        <label for="venue-desc">Description</label>
        <textarea id="venue-desc" bind:value={form.description} placeholder="Notes about this venue..." rows="3" maxlength="5000"></textarea>
      </div>

      <div class="btn-row">
        <button class="btn-secondary" on:click={closeForm}>Cancel</button>
        {#if editing}
          <button class="btn-danger" on:click={deleteRecord} disabled={deleteLoad}>
            {deleteLoad ? 'DELETING...' : 'DELETE'}
          </button>
        {/if}
        <button class="btn-primary" on:click={submitForm} disabled={formLoad}>
          {formLoad ? 'SAVING...' : editing ? 'SAVE CHANGES' : 'ADD VENUE'}
        </button>
      </div>
      {#if deleteError}<div class="form-error">{deleteError}</div>{/if}
    {/if}
  </div>
{/if}

<!-- ── Dedupe view ───────────────────────────────────────────────────── -->
{#if dedupeOpen}
  <div class="dedupe-view">
    <div class="dedupe-header">
      <div class="dedupe-title">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M8 7h12M8 12h8M8 17h4"/><polyline points="16 3 21 8 16 13"/>
        </svg>
        Resolve duplicates — {dupeGroups.length} group{dupeGroups.length > 1 ? 's' : ''}
      </div>
      <button class="btn-dismiss" on:click={closeDedupe}>✕ Back to list</button>
    </div>

    {#if mergeGroupError}
      <div class="form-error">{mergeGroupError}</div>
    {/if}

    {#if dupeGroups.length === 0}
      <div class="state-msg empty">No duplicates — all clean.</div>
    {:else}
      {#each dupeGroups as group, i}
        <div class="dupe-group">
          <div class="dupe-group-header">
            <span class="dupe-group-name">"{group[0].name}"</span>
            <span class="dupe-group-count">{group.length} entries</span>
          </div>
          <div class="dupe-group-hint">Select the canonical entry to keep. Others will be merged into it.</div>
          <div class="dupe-candidates">
            {#each group as r}
              <!-- svelte-ignore a11y-click-events-have-key-events -->
              <div
                class="dupe-candidate"
                class:selected={canonicalIds[i] === r.id}
                role="radio"
                aria-checked={canonicalIds[i] === r.id}
                tabindex="0"
                on:click={() => canonicalIds[i] = r.id}
                on:keydown={e => e.key === 'Enter' && (canonicalIds[i] = r.id)}
              >
                <div class="dupe-radio">
                  {#if canonicalIds[i] === r.id}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                      <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4" fill="currentColor"/>
                    </svg>
                  {:else}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"/>
                    </svg>
                  {/if}
                </div>
                <div class="dupe-details">
                  <span class="dupe-id">#{r.id}</span>
                  {#if r.city}<span class="dupe-pill">{r.city}</span>{/if}
                  {#if r.country}<span class="dupe-pill">{r.country}</span>{/if}
                  {#if r.capacity}<span class="dupe-pill">{r.capacity.toLocaleString()} cap</span>{/if}
                  {#if r.address}<span class="dupe-pill has-data">address ✓</span>{/if}
                  {#if !r.city && !r.country && !r.capacity && !r.address}
                    <span class="dupe-empty">no details</span>
                  {/if}
                </div>
                {#if canonicalIds[i] === r.id}
                  <span class="dupe-keep-label">KEEP</span>
                {:else}
                  <span class="dupe-absorb-label">ABSORB</span>
                {/if}
              </div>
            {/each}
          </div>
          <div class="dupe-group-actions">
            <button
              class="btn-merge-group"
              disabled={mergeGroupLoad && mergeGroupIdx === i}
              on:click={() => mergeGroup(i)}
            >
              {mergeGroupLoad && mergeGroupIdx === i ? 'Merging...' : `Merge ${group.length - 1} into #${canonicalIds[i]}`}
            </button>
          </div>
        </div>
      {/each}
    {/if}
  </div>

<!-- ── Record list ───────────────────────────────────────────────────── -->
{:else if !showForm}
  {#if records.length === 0}
    <div class="state-msg empty">No venues yet.</div>
  {:else}
    <div class="list">
      {#each records as record (record.id)}
        <div class="venue-block" class:expanded={expandedVenueId === record.id} class:dupe-row={isDuplicate(record)}>
          <div class="list-row venue-row">
            <button
              class="expand-btn" class:open={expandedVenueId === record.id}
              on:click|stopPropagation={() => toggleVenueExpand(record)}
              title="Manage stages"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <div class="row-body" role="button" tabindex="0"
              on:click={() => openEdit(record)}
              on:keydown={e => e.key === 'Enter' && openEdit(record)}
            >
              <span class="row-label">{record.name}</span>
              {#if isDuplicate(record)}
                <span class="dupe-indicator" title="Name conflict">≡</span>
              {/if}
              {#if record.city || record.country}
                <span class="row-sub">{[record.city, record.country].filter(Boolean).join(', ')}</span>
              {/if}
              <svg class="row-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </div>
          </div>

          {#if expandedVenueId === record.id}
            <div class="stage-panel">
              {#if venueStageLoad[record.id]}
                <div class="stage-loading"><span class="spinner-sm"></span></div>
              {:else if venueStages[record.id]?.length > 0}
                <div class="stage-list">
                  {#each venueStages[record.id] as stage}
                    <div class="stage-row">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <rect x="2" y="14" width="20" height="8" rx="1"/>
                        <path d="M6 14V8a6 6 0 0 1 12 0v6"/>
                      </svg>
                      <span class="stage-name">{stage.name}</span>
                      {#if stage.capacity}
                        <span class="stage-cap">{stage.capacity.toLocaleString()}</span>
                      {/if}
                      <button class="stage-delete"
                        on:click|stopPropagation={() => deleteStage(record.id, stage.id)}
                        title="Delete stage">✕</button>
                    </div>
                  {/each}
                </div>
              {:else}
                <span class="stage-empty">No stages yet</span>
              {/if}

              {#if addStageVenueId === record.id}
                <div class="add-stage-form">
                  {#if stageError}<div class="stage-form-error">{stageError}</div>{/if}
                  <div class="stage-inputs">
                    <input class="stage-input" type="text" bind:value={stageName} placeholder="Stage name" maxlength="100" />
                    <input class="stage-input stage-input-sm" type="number" bind:value={stageCapacity} placeholder="Cap." min="1" />
                    <button class="btn-stage-save" on:click={() => submitAddStage(record.id)} disabled={stageLoad}>
                      {stageLoad ? '…' : 'Add'}
                    </button>
                    <button class="btn-stage-cancel" on:click={closeAddStage}>✕</button>
                  </div>
                </div>
              {:else}
                <button class="btn-add-stage" on:click={() => openAddStage(record.id)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Add Stage
                </button>
              {/if}
            </div>
          {/if}
        </div>
      {/each}
    </div>
  {/if}
{/if}

<style>
  /* Venue-specific layout */
  .venue-block { border-bottom: 1px solid #0f1a00; }
  .venue-block:last-child { border-bottom: none; }
  .venue-block.expanded { background: #050b00; }
  .venue-row { border-bottom: none !important; gap: 0; padding-left: 0; }

  .expand-btn {
    width: 36px; height: 100%; flex-shrink: 0; background: none; border: none;
    color: #2a5200; cursor: pointer; display: flex; align-items: center; justify-content: center;
    padding: 0.7rem 0.5rem; transition: color 0.15s;
  }
  .expand-btn svg { width: 14px; height: 14px; transition: transform 0.2s; }
  .expand-btn.open svg { transform: rotate(180deg); }
  .expand-btn:hover { color: #6db800; }

  .row-body {
    flex: 1; display: flex; align-items: center; gap: 0.75rem;
    padding: 0.7rem 0.85rem 0.7rem 0.25rem; cursor: pointer; min-width: 0;
  }

  /* Stage panel */
  .stage-panel {
    padding: 0.5rem 0.85rem 0.65rem 2.35rem;
    border-top: 1px solid #0f1a00; background: #060c00;
  }
  .stage-loading { padding: 0.4rem 0; }
  .spinner-sm { display: inline-block; width: 10px; height: 10px; border: 1.5px solid #1e3d00; border-top-color: #6db800; border-radius: 50%; animation: spin 0.7s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }

  .stage-list { display: flex; flex-direction: column; gap: 0.2rem; margin-bottom: 0.4rem; }
  .stage-row {
    display: flex; align-items: center; gap: 0.5rem;
    padding: 0.3rem 0; border-bottom: 1px solid #0a1200;
  }
  .stage-row:last-child { border-bottom: none; }
  .stage-row svg { width: 12px; height: 12px; flex-shrink: 0; stroke: #2a5200; }
  .stage-name { flex: 1; font-family: 'Rajdhani', sans-serif; font-weight: 600; font-size: 0.8rem; color: #c8c8c8; }
  .stage-cap { font-family: 'Rajdhani', sans-serif; font-size: 0.6rem; color: #3a6b00; letter-spacing: 0.08em; }
  .stage-delete {
    background: none; border: none; color: #1e3d00; cursor: pointer; font-size: 0.65rem;
    padding: 0.1rem 0.25rem; transition: color 0.15s; line-height: 1;
  }
  .stage-delete:hover { color: #cc3300; }
  .stage-empty { font-family: 'Rajdhani', sans-serif; font-size: 0.6rem; color: #1e3d00; letter-spacing: 0.1em; font-style: italic; display: block; margin-bottom: 0.4rem; }

  .btn-add-stage {
    display: flex; align-items: center; gap: 0.3rem; margin-top: 0.35rem;
    background: none; border: 1px dashed #1e3d00; color: #2a5200;
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.55rem;
    letter-spacing: 0.15em; text-transform: uppercase; padding: 0.3rem 0.6rem;
    border-radius: 2px; cursor: pointer; transition: border-color 0.15s, color 0.15s;
  }
  .btn-add-stage svg { width: 10px; height: 10px; }
  .btn-add-stage:hover { border-color: #3a6b00; color: #6db800; }
  .add-stage-form { margin-top: 0.35rem; }
  .stage-form-error { font-family: 'Rajdhani', sans-serif; font-size: 0.6rem; color: #cc6633; margin-bottom: 0.3rem; }
  .stage-inputs { display: flex; gap: 0.4rem; align-items: center; }
  .stage-input {
    background: #0a0a0a; border: 1px solid #1e3d00; border-radius: 2px;
    color: #c8c8c8; font-family: 'Rajdhani', sans-serif; font-size: 0.85rem; font-weight: 600;
    padding: 0.4rem 0.55rem; outline: none; flex: 1; min-width: 0; transition: border-color 0.2s;
  }
  .stage-input:focus { border-color: #3a6b00; }
  .stage-input-sm { flex: 0 0 5rem; }
  .btn-stage-save {
    background: #1a3300; border: 1px solid #3a6b00; color: #6db800;
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.6rem;
    letter-spacing: 0.15em; text-transform: uppercase; padding: 0.4rem 0.65rem;
    border-radius: 2px; cursor: pointer; transition: background 0.15s; white-space: nowrap;
  }
  .btn-stage-save:hover { background: #223d00; }
  .btn-stage-save:disabled { opacity: 0.4; cursor: not-allowed; }
  .btn-stage-cancel { background: none; border: none; color: #2a5200; cursor: pointer; font-size: 0.75rem; padding: 0.2rem; transition: color 0.15s; }
  .btn-stage-cancel:hover { color: #cc3300; }
</style>
